import {getSiteUrl} from "@/helpers/functions";

export interface ProductGroupVariant {
    name: string;
    url: string;
    image?: string;
    sku?: string;
}

export interface SeoSchemaProductGroupProps {
    name: string;
    description: string;
    url?: string;
    variesBy: string[];
    variants: ProductGroupVariant[];
    brandName?: string;
    productGroupID?: string;
}

const toAbsoluteUrl = (siteUrl: string, url: string): string => {
    if (/^https?:\/\//i.test(url)) return url;
    const path = url.startsWith("/") ? url : `/${url}`;
    return `${siteUrl}${path}`;
};

export function SeoSchemaProductGroup({
    name,
    description,
    url,
    variesBy,
    variants,
    brandName = "ACTIVE",
    productGroupID,
}: SeoSchemaProductGroupProps) {
    if (!variants.length) return null;

    const siteUrl = getSiteUrl();
    const absUrl = url ? toAbsoluteUrl(siteUrl, url) : undefined;

    const hasVariant = variants.map(variant => {
        const variantUrl = toAbsoluteUrl(siteUrl, variant.url);
        const entry: Record<string, unknown> = {
            "@type": "Product",
            "@id": variantUrl,
            "name": variant.name,
            "url": variantUrl,
            "brand": {"@type": "Brand", "name": brandName},
            "offers": {
                "@type": "Offer",
                "url": variantUrl,
                "availability": "https://schema.org/InStock",
                "businessFunction": "https://schema.org/Sell",
                "eligibleCustomerType": "http://purl.org/goodrelations/v1#Business",
                "priceCurrency": "USD",
                "priceSpecification": {
                    "@type": "PriceSpecification",
                    "priceCurrency": "USD",
                    "valueAddedTaxIncluded": false,
                },
                "seller": {"@id": `${siteUrl}/#organization`},
            },
        };
        if (variant.image) entry.image = toAbsoluteUrl(siteUrl, variant.image);
        if (variant.sku) entry.sku = variant.sku;
        return entry;
    });

    const schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "ProductGroup",
        "name": name,
        "description": description,
        "brand": {"@type": "Brand", "name": brandName},
        "variesBy": variesBy,
        "hasVariant": hasVariant,
    };

    if (absUrl) schema.url = absUrl;
    if (productGroupID) schema.productGroupID = productGroupID;

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
        />
    );
}
